import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  Button,
  Menu,
  MenuItem,
  Avatar,
  Divider
} from '@mui/material';
import { 
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  SupervisorAccount as SuperAdminIcon
} from '@mui/icons-material';
import Sidebar from './Sidebar';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 240;

// Page titles mapping
const pageTitles = {
  '/admin/dashboard': 'Dashboard Overview',
  '/admin/submissions': 'Submission Management',
  '/admin/events': 'Event Management',
  '/admin/kiosk': 'Kiosk Launcher',
  '/admin/settings': 'Settings & Configuration',
  '/admin/branches': 'Branch Management',
  '/admin/users': 'User Management'
};

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const currentTitle = pageTitles[location.pathname] || 'Broker Union Platform';

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const getBranchDisplay = () => {
    if (isSuperAdmin()) {
      return 'Super Administrator';
    }
    return user?.branch?.displayName || 'No Branch';
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content Area */}
      <Box component="main" sx={{ flexGrow: 1, bgcolor: '#f9f9f9', minHeight: '100vh' }}>
        {/* Top Bar */}
        <AppBar
          position="static"
          elevation={0}
          color="inherit"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            borderBottom: '1px solid #ddd',
            backgroundColor: 'white'
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {currentTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getBranchDisplay()}
              </Typography>
            </Box>

            {/* User Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {isSuperAdmin() && (
                <SuperAdminIcon color="warning" titleAccess="Super Administrator" />
              )}
              
              <Button
                startIcon={<AccountIcon />}
                onClick={handleMenuOpen}
                sx={{ textTransform: 'none' }}
              >
                {user?.name || 'User'}
              </Button>
              
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="subtitle2">{user?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user?.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user?.role}
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Routed Page Content */}
        <Box>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;